import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import numeral from 'numeral';
import Text from '@deprecated-ui/common/Text';
import Table from '@deprecated-ui/calendar/Table';
import Image from 'global/Image';
import { symbolCustomRender } from '../utils';

import {
  CalendarTodayDataIndexEnums,
  CalendarTodayEnums,
  ICalendarStreamline,
  ICalendarToday,
  TData,
} from '../type';
import { effects, selectors } from '../slice';

import CalendarBody from '../CalendarBody/CalendarBody';
import { EmptyCalendarWrapper, SubTitle } from '../styled';
import { CALENDAR_TODAY_TYPE, DATE_FORMAT } from '../constants';

const CURRENCY_LIST = {
  CURRENCY_IDR: 'Rp ',
  CURRENCY_USD: 'USD ',
  CURRENCY_UNSPECIFIED: '',
};

const DEFAULT_TODAY = format(new Date(), DATE_FORMAT);
const TEXT_CALENDAR_TODAY = CalendarTodayEnums;
const DATA_INDEX_CALENDAR_TODAY = CalendarTodayDataIndexEnums;

const dataGenerator = {
  [CALENDAR_TODAY_TYPE.ECONOMIC.INDEX]: (rawData: any) => {
    const result = rawData.map((eco, index) => {
      let time = '';
      if (eco.econcal_time) {
        const [hours, minutes] = eco.econcal_time.split(':');
        time = `${hours}:${minutes}`;
      }
      return {
        ...eco,
        [DATA_INDEX_CALENDAR_TODAY.ECONCAL_EDITTED_TIME]: time,
        [DATA_INDEX_CALENDAR_TODAY.ECONCAL_EDITTED_SYMBOL]: `${eco.econcal_item}  (${eco.econcal_month})`,
      };
    });
    return result;
  },
  [CALENDAR_TODAY_TYPE.DIVIDEND.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.STOCKSPLIT.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.STOCK_REVERSE.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.RIGHT_ISSUE.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.WARRANT.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.BONUS.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.TENDER_OFFER.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.RUPS.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.PUBEX.INDEX]: (rawData: any) => rawData,
  [CALENDAR_TODAY_TYPE.IPO.INDEX]: (rawData: any) => {
    const result = rawData.map((ipo) => ({
      ...ipo,
      [DATA_INDEX_CALENDAR_TODAY.IPO_PRICE]: JSON.parse(ipo.ipo_data).Price,
    }));
    return result;
  },
};

const columnSymbolRender = symbolCustomRender;

const CalendarStreamline = ({
  title,
  dataSources,
  columns,
  rowKey,
}: ICalendarStreamline) => (
  <>
    <SubTitle>{title}</SubTitle>
    <Table
      showHeader={false}
      dataSource={dataSources}
      columns={columns}
      rowKey={rowKey}
    />
  </>
);

const CalendarToday = () => {
  const [calendar, setCalendar] = useState<ICalendarToday>();
  const [economicToday, setEconomicToday] = useState([]);
  const [dividendToday, setDividendToday] = useState([]);
  const [stocksplitToday, setStocksplitToday] = useState([]);
  const [reverseSplitToday, setReverseSplitToday] = useState([]);
  const [rightIssueToday, setRightIssueToday] = useState([]);
  const [warrantToday, setWarrantToday] = useState([]);
  const [bonusToday, setBonusToday] = useState([]);
  const [tenderofferToday, setTenderofferToday] = useState([]);
  const [rupsToday, setRupsToday] = useState([]);
  const [pubexToday, setPubexToday] = useState([]);
  const [ipoToday, setIpoToday] = useState([]);
  const [today, setToday] = useState(DEFAULT_TODAY);

  const dispatch = useDispatch();
  const { calendarToday } = useSelector(selectors);

  const getCalendarItem = (array: Array<any>): string => {
    const found = array.find((item) => !!item?.[today]);
    return found?.[today] || '';
  };

  const columnDividendRender = (_text, record) => {
    const dividendList = [
      { [record.dividend_cumdate]: TEXT_CALENDAR_TODAY.CUM_DATE },
      { [record.dividend_exdate]: TEXT_CALENDAR_TODAY.EX_DATE },
    ];
    const result = getCalendarItem(dividendList);
    return {
      children: result,
    };
  };

  const columnStocksplitRender = (_text, record) => {
    const stocksplitList = [
      { [record.stocksplit_cumdate]: TEXT_CALENDAR_TODAY.CUM_DATE },
      { [record.stocksplit_exdate]: TEXT_CALENDAR_TODAY.EX_DATE },
    ];
    const result = getCalendarItem(stocksplitList);
    return {
      children: result,
    };
  };

  const columnRightIssueRender = (_text, record) => {
    const rightIssueList = [
      { [record.rightissue_cumdate]: TEXT_CALENDAR_TODAY.CUM_DATE },
      { [record.rightissue_exdate]: TEXT_CALENDAR_TODAY.EX_DATE },
      { [record.rightissue_trading_start]: TEXT_CALENDAR_TODAY.TRADING_START },
      { [record.rightissue_trading_end]: TEXT_CALENDAR_TODAY.TRADING_END },
    ];
    const result = getCalendarItem(rightIssueList);
    return {
      children: result,
    };
  };

  const columnWarrantRender = (_text, record) => {
    const warrantList = [
      { [record.wrant_trading_from]: TEXT_CALENDAR_TODAY.TRADING_START },
      { [record.wrant_trading_end]: TEXT_CALENDAR_TODAY.TRADING_END },
      { [record.wrant_exc_from]: TEXT_CALENDAR_TODAY.EXERCISE_START },
      { [record.wrant_exc_end]: TEXT_CALENDAR_TODAY.EXERCISE_END },
    ];
    const result = getCalendarItem(warrantList);
    return {
      children: result,
    };
  };

  const columnTenderOfferRender = (_text, record) => {
    const tenderOfferList = [
      { [record.tender_start]: TEXT_CALENDAR_TODAY.OFFERING_START },
      { [record.tender_end]: TEXT_CALENDAR_TODAY.OFFERING_END },
      { [record.tender_paydate]: TEXT_CALENDAR_TODAY.PAY_DATE },
    ];
    const result = getCalendarItem(tenderOfferList);
    return {
      children: result,
    };
  };

  const renderPrice = (text) => numeral(Number(text)).format('0,0[.]00');

  const columnGenerator = {
    [CALENDAR_TODAY_TYPE.ECONOMIC.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.ECONCAL_EDITTED_SYMBOL,
        width: '20%',
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.ECONCAL_EDITTED_TIME,
      },
    ],
    [CALENDAR_TODAY_TYPE.DIVIDEND.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.DIVIDEND_VALUE,
        render: (text: string, record: any) => ({
          children: `${
            text ||
            `${CURRENCY_LIST[record.dividend_currency]}${record.dividend_value}`
          } / Saham`,
        }),
      },
      {
        render: columnDividendRender,
      },
    ],
    [CALENDAR_TODAY_TYPE.STOCKSPLIT.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      { dataIndex: DATA_INDEX_CALENDAR_TODAY.STOCKSPLIT_RATIO },
      { render: columnStocksplitRender },
    ],
    [CALENDAR_TODAY_TYPE.STOCK_REVERSE.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      { dataIndex: DATA_INDEX_CALENDAR_TODAY.STOCKSPLIT_RATIO },
      { render: columnStocksplitRender },
    ],
    [CALENDAR_TODAY_TYPE.RIGHT_ISSUE.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.RIGHTISSUE_RATIO,
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.RIGHTISSUE_PRICE,
        render: (text) => ({
          children: `Rp. ${renderPrice(text)}`,
        }),
      },
      {
        render: columnRightIssueRender,
      },
    ],
    [CALENDAR_TODAY_TYPE.WARRANT.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.WARRANT_EXC_PRICE,
        render: (text) => ({
          children: `Rp. ${renderPrice(text)}`,
        }),
      },
      {
        render: columnWarrantRender,
      },
    ],
    [CALENDAR_TODAY_TYPE.BONUS.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      { dataIndex: DATA_INDEX_CALENDAR_TODAY.BONUS_RATIO },
      { render: columnStocksplitRender },
    ],
    [CALENDAR_TODAY_TYPE.TENDER_OFFER.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.TENDER_PRICE,
        render: (text) => ({
          children: `Rp. ${Number(text).toLocaleString()}`,
        }),
      },
      {
        render: columnTenderOfferRender,
      },
    ],
    [CALENDAR_TODAY_TYPE.RUPS.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.RUPS_TIME,
      },
    ],
    [CALENDAR_TODAY_TYPE.PUBEX.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_SYMBOL,
        width: '20%',
        render: columnSymbolRender,
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.PUEXP_TIME,
        render: (text) => ({
          children: text?.slice(0, 5) || '',
        }),
      },
    ],
    [CALENDAR_TODAY_TYPE.IPO.INDEX]: [
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.COMPANY_NAME,
        width: '20%',
      },
      {
        dataIndex: DATA_INDEX_CALENDAR_TODAY.IPO_PRICE,
        render: (text) => ({
          children: `Rp. ${Number(text.replace(',', '')).toLocaleString()}`,
        }),
      },
    ],
  };

  const getDataFromDataGenerator = (
    calendarData: ICalendarToday,
    calendarType: TData,
  ): Array<any> => {
    if (calendarData?.data) {
      const { [calendarType]: newCalendarType } = calendarData.data;
      return dataGenerator[calendarType](newCalendarType);
    }
    return [];
  };

  useEffect(() => {
    dispatch(effects.getCalendarToday());
  }, [dispatch]);

  useEffect(() => {
    setCalendar(calendarToday);
  }, [calendarToday]);

  useEffect(() => {
    setEconomicToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.ECONOMIC.INDEX as TData,
      ),
    );

    setDividendToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.DIVIDEND.INDEX as TData,
      ),
    );

    setStocksplitToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.STOCKSPLIT.INDEX as TData,
      ),
    );

    setReverseSplitToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.STOCK_REVERSE.INDEX as TData,
      ),
    );

    setRightIssueToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.RIGHT_ISSUE.INDEX as TData,
      ),
    );

    setWarrantToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.WARRANT.INDEX as TData,
      ),
    );

    setBonusToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.BONUS.INDEX as TData,
      ),
    );

    setTenderofferToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.TENDER_OFFER.INDEX as TData,
      ),
    );

    setRupsToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.RUPS.INDEX as TData,
      ),
    );

    setPubexToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.PUBEX.INDEX as TData,
      ),
    );

    setIpoToday(() =>
      getDataFromDataGenerator(
        calendar,
        CALENDAR_TODAY_TYPE.IPO.INDEX as TData,
      ),
    );

    setToday(() => {
      if (calendar?.data) {
        return calendar.data?.today;
      }
      return DEFAULT_TODAY;
    });
  }, [calendar]);

  const calendarDataList = {
    [CALENDAR_TODAY_TYPE.ECONOMIC.INDEX]: {
      title: CALENDAR_TODAY_TYPE.ECONOMIC.TITLE,
      dataSources: economicToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.ECONOMIC.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.ECONOMIC.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.DIVIDEND.INDEX]: {
      title: CALENDAR_TODAY_TYPE.DIVIDEND.TITLE,
      dataSources: dividendToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.DIVIDEND.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.DIVIDEND.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.STOCKSPLIT.INDEX]: {
      title: CALENDAR_TODAY_TYPE.STOCKSPLIT.TITLE,
      dataSources: stocksplitToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.STOCKSPLIT.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.STOCKSPLIT.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.STOCK_REVERSE.INDEX]: {
      title: CALENDAR_TODAY_TYPE.STOCK_REVERSE.TITLE,
      dataSources: reverseSplitToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.STOCK_REVERSE.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.STOCK_REVERSE.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.RIGHT_ISSUE.INDEX]: {
      title: CALENDAR_TODAY_TYPE.RIGHT_ISSUE.TITLE,
      dataSources: rightIssueToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.RIGHT_ISSUE.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.RIGHT_ISSUE.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.WARRANT.INDEX]: {
      title: CALENDAR_TODAY_TYPE.WARRANT.TITLE,
      dataSources: warrantToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.WARRANT.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.WARRANT.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.BONUS.INDEX]: {
      title: CALENDAR_TODAY_TYPE.BONUS.TITLE,
      dataSources: bonusToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.BONUS.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.BONUS.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.TENDER_OFFER.INDEX]: {
      title: CALENDAR_TODAY_TYPE.TENDER_OFFER.TITLE,
      dataSources: tenderofferToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.TENDER_OFFER.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.TENDER_OFFER.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.RUPS.INDEX]: {
      title: CALENDAR_TODAY_TYPE.RUPS.TITLE,
      dataSources: rupsToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.RUPS.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.RUPS.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.PUBEX.INDEX]: {
      title: CALENDAR_TODAY_TYPE.PUBEX.TITLE,
      dataSources: pubexToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.PUBEX.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.PUBEX.ROW_KEY,
    },
    [CALENDAR_TODAY_TYPE.IPO.INDEX]: {
      title: CALENDAR_TODAY_TYPE.IPO.TITLE,
      dataSources: ipoToday,
      columns: columnGenerator[CALENDAR_TODAY_TYPE.IPO.INDEX],
      rowKey: CALENDAR_TODAY_TYPE.IPO.ROW_KEY,
    },
  };

  const renderCalendarStreamline = (calendarName: string) => {
    const { title, dataSources, columns, rowKey }: ICalendarStreamline =
      calendarDataList[calendarName];
    if (dataSources.length) {
      return CalendarStreamline({
        title,
        dataSources,
        columns,
        rowKey,
      });
    }
    return null;
  };

  const isEmpty = useMemo(() => {
    if (
      calendar?.data &&
      calendar?.data[CALENDAR_TODAY_TYPE.ECONOMIC.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.DIVIDEND.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.STOCKSPLIT.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.STOCK_REVERSE.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.RIGHT_ISSUE.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.WARRANT.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.BONUS.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.TENDER_OFFER.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.RUPS.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.PUBEX.INDEX]?.length === 0 &&
      calendar?.data[CALENDAR_TODAY_TYPE.IPO.INDEX]?.length === 0
    ) {
      return true;
    }
    return false;
  }, [calendar]);

  if (isEmpty) {
    return (
      <EmptyCalendarWrapper>
        <Image src="/illustration/calendar-empty.svg" alt="empty" />
        {/* @ts-expect-error */}
        <Text mt="10px" color="#858585">
          There Are No Scheduled Events Today
        </Text>
      </EmptyCalendarWrapper>
    );
  }

  return (
    <CalendarBody>
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.ECONOMIC.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.DIVIDEND.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.STOCKSPLIT.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.STOCK_REVERSE.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.RIGHT_ISSUE.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.WARRANT.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.BONUS.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.TENDER_OFFER.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.RUPS.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.PUBEX.INDEX)}
      {renderCalendarStreamline(CALENDAR_TODAY_TYPE.IPO.INDEX)}
    </CalendarBody>
  );
};

export default CalendarToday;
